import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { BRAND, CONTACT } from '@/content';
import { track } from '@/lib/track';
import { scrollToTarget } from '@/lib/scrollTo';

const LINKS = [
  { label: 'Overview', href: '#overview' },
  { label: 'Explore', href: '#explore' },
  { label: 'Homes', href: '#homes' },
  { label: 'Connectivity', href: '#connectivity' },
  { label: 'Plans', href: '#plans' },
  { label: 'Specifications', href: '#specifications' },
];

// first visible image of each section — preloaded the moment a nav link is clicked,
// so the destination never appears blank while the smooth scroll lands
const SECTION_ASSET = {
  '#explore': '/assets/ch02-elevation-spread-2400.webp',
  '#homes': '/assets/ch03-ventilated-spread.webp',
  '#connectivity': '/assets/map-connectivity.webp',
  '#plans': '/assets/plan-first.webp',
  '#specifications': '/assets/room-living.webp',
};

export default function Header({ onEnquire }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const src = SECTION_ASSET[href];
    if (src) { const im = new Image(); im.src = src; }
    const el = document.querySelector(href);
    if (!el) return;
    track('nav_click', { target: href });
    scrollToTarget(el, -72);
  };

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn();
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled ? 'border-b border-[#E4E6EB] bg-white/90 shadow-[0_8px_30px_-18px_rgba(28,29,33,0.35)] backdrop-blur-md' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <a href="#overview" data-testid="header-logo-link" aria-label="Metro One Navade — back to top" className="flex items-center" onClick={(e) => go(e, '#overview')}>
            <span className={`rounded-xl px-2 py-1 ${scrolled ? '' : 'bg-white/85 backdrop-blur-sm'}`}>
              <img src={BRAND.logo} alt="Codename Metro One — Navade" className="h-8 w-auto sm:h-9" />
            </span>
          </a>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase()}-link`}
                onClick={(e) => go(e, l.href)}
                className="font-display text-[11px] font-bold uppercase tracking-[0.22em] text-[#404145] transition-colors hover:text-[#C92F4B]"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${CONTACT.phone}`}
              data-testid="header-call-btn"
              aria-label={`Call ${CONTACT.phoneDisplay}`}
              onClick={() => track('call_click', { placement: 'header' })}
              className="hidden h-11 w-11 items-center justify-center rounded-full border border-[#E4E6EB] bg-white/85 text-[#404145] backdrop-blur-sm transition-colors hover:text-[#C92F4B] sm:flex"
            >
              <Phone size={17} />
            </a>
            <button
              data-testid="header-book-visit-btn"
              onClick={() => { track('book_visit', { placement: 'header' }); onEnquire(); }}
              className="btn-primary hidden !px-6 !py-2.5 !text-xs md:inline-flex"
            >
              Book a Site Visit
            </button>
            <button
              data-testid="header-enquire-btn"
              onClick={() => onEnquire()}
              className="btn-primary !px-5 !py-2.5 !text-[11px] md:hidden"
            >
              Enquire
            </button>
            <button
              data-testid="header-menu-btn"
              aria-label="Open menu"
              aria-expanded={open}
              onClick={() => setOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E4E6EB] bg-white/85 text-[#1C1D21] backdrop-blur-sm lg:hidden"
            >
              <Menu size={19} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] flex flex-col bg-white/97 backdrop-blur-xl"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.25 }}
          >
            <div className="flex h-16 items-center justify-between px-4">
              <img src={BRAND.logo} alt="Codename Metro One — Navade" className="h-8 w-auto" />
              <button
                data-testid="mobile-menu-close-btn"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E4E6EB]"
              >
                <X size={19} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                  onClick={(e) => go(e, l.href)}
                  className="border-b border-[#E4E6EB] py-4 font-display text-2xl font-extrabold uppercase tracking-tight text-[#1C1D21]"
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <div className="safe-bottom px-8 pb-8">
              <p className="text-xs text-[#646A72]">MahaRERA Registration No. {BRAND.rera}</p>
              <button
                data-testid="mobile-menu-visit-btn"
                onClick={() => { setOpen(false); onEnquire(); }}
                className="btn-primary mt-4 w-full"
              >
                Book a Site Visit
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
